/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import { GetServerSideProps } from 'next';
import { PageRendererComponent, prePageCall } from '@marriott/mi-headless-utils';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';
import {
  fetchMBEData,
  getSubDirectoryLocale,
  isPreviewRequiredForOfferVersion,
  OFFERS_HUB_PATH,
  updateMBEDataLayer,
  NEXT_PUBLIC_ENV_KEYS,
  isNonEmptyString,
} from '@marriott/mi-offers-components';

import OfferSignatures from '../../../signatures/offerSignatures.json';

const { SESSION_APP_DOMAIN_URL } = process.env;

function pageRenderer(props) {
  console.log('[MBE] props ', props);
  return <PageRendererComponent {...props} />;
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const mbeSpecificData = async (context: any, _: any, interceptorResponse: any) => {
  const { req, res } = context;
  const { log } = global.loggerInstance('mbeSpecificData');
  log.debug('[mbeSpecificData] interceptorResponse -', JSON.stringify(interceptorResponse));
  log.debug(
    '[mbeSpecificData] interceptorResponse pageManagerRequest global-',
    JSON.stringify(interceptorResponse?.requestAttributes?.pageManagerRequest?.global)
  );
  const currentLocale = interceptorResponse?.requestAttributes?.pageManagerRequest?.global?.localeKey?.replace(
    '_',
    '-'
  );
  log.debug('mbeSpecificData currentLocale', currentLocale);
  let pageSpecificDatalayer = {};
  let mbeSpecificData: any = '';
  const isAuthorMode = req?.url?.includes('isAuthorMode') ?? false;
  const resolvedUrlString: string = context.resolvedUrl;
  const isValidUrl = isPreviewRequiredForOfferVersion(resolvedUrlString); //validate preview url before making BE service call.

  const [featureFlags, featureFlagLogo, featureFlagAemltsHost] = await Promise.all([
    // Feature flag call for MBOP
    fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_MBOP, {
      target: TARGETS.APP.OFFERS_RENDERER,
    }),
    // Feature flag call for logo automation
    fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION, {
      target: TARGETS.APP.OFFERS_RENDERER,
    }),
    // Feature flag to switch AEM host to LTS host
    fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_OFFERS_RENDERER_AEMLTS_HOST, {
      target: TARGETS.APP.OFFERS_RENDERER,
      enableStream: true,
    }),
  ]);
  const useAemLtsHost = featureFlagAemltsHost?.experience_mcom_offers_renderer_aemlts_host;
  const aemHost = useAemLtsHost ? process.env.NEXT_PUBLIC_AEM_LTS_HOST : process.env.NEXT_PUBLIC_AEM_HOST;

  // Allow query param to override feature flag - temporary for testing purposes
  let mbeDynamicWorkflow: any = true;
  if (context?.query?.mbeDynamicWorkflow) {
    mbeDynamicWorkflow = context.query.mbeDynamicWorkflow === 'true' || context.query.mbeDynamicWorkflow === true;
    log.debug('MBE | mbeDynamicWorkflow from query param', mbeDynamicWorkflow);
  } else {
    mbeDynamicWorkflow =
      (featureFlags?.experience_mcom_mbop &&
        typeof featureFlags.experience_mcom_mbop === 'object' &&
        'mbeDynamicWorkflow' in featureFlags.experience_mcom_mbop &&
        (featureFlags.experience_mcom_mbop as Record<string, unknown>).mbeDynamicWorkflow) ||
      true;
    log.debug('MBE | mbeDynamicWorkflow', mbeDynamicWorkflow);
  }

  const shouldSkipMbeServiceApiCall =
    isAuthorMode ||
    !resolvedUrlString?.includes('last-minute-hotel-deals') ||
    !mbeDynamicWorkflow ||
    resolvedUrlString?.includes('terms-and-conditions');
  log.debug('MBE | shouldSkipMbeServiceApiCall', shouldSkipMbeServiceApiCall);
  if (shouldSkipMbeServiceApiCall) {
    mbeSpecificData = '';
  } else if (isValidUrl) {
    if (!resolvedUrlString?.includes('offerid')) {
      const mbeFallbackOfferId =
        ((featureFlags.experience_mcom_mbop as Record<string, unknown>).mbeFallbackOfferId as string) || 'off-160715';
      const hasQuery = context?.resolvedUrl?.includes('?');
      const separator = hasQuery
        ? context.resolvedUrl.endsWith('?') || context.resolvedUrl.endsWith('&')
          ? ''
          : '&'
        : '?';
      context.resolvedUrl += `${separator}offerid=${mbeFallbackOfferId}`;
      log.debug('MBE | updated resolvedUrlString', context.resolvedUrl);
    }
    mbeSpecificData = await fetchMBEData(context, currentLocale);
    log.debug('[mbeSpecificData] mbeSpecificData', mbeSpecificData);
    context.mbeOffersClusterCode = mbeSpecificData?.response?.responseObject?.offers?.mbeOfferById?.offers?.clusterCode;
    context.mbeOffersCycleStatus = mbeSpecificData?.response?.responseObject?.offers?.mbeOfferById?.offers?.cycleStatus;
    context.mbeDynamicWorkflow = mbeDynamicWorkflow;
    log.debug('[mbeSpecificData] context', context);
    log.debug('[MBE] clusterCode ', context.mbeOffersClusterCode);
  } else {
    // Redirect if not a valid preview URL
    const subDomain = getSubDirectoryLocale(currentLocale);
    res?.writeHead(307, { Location: `${subDomain}${OFFERS_HUB_PATH}` });
    res?.end();
    return {};
  }

  if (!isAuthorMode && mbeSpecificData && Object.keys(mbeSpecificData?.response)?.length) {
    pageSpecificDatalayer = updateMBEDataLayer(mbeSpecificData?.response);
  }

  return {
    operationSignatures: OfferSignatures,
    featureFlagAemltsHost: featureFlagAemltsHost ?? null,
    aemHost: aemHost ?? null,
    nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
    uri: mbeSpecificData?.response?.uri,
    productSpecificResponse: mbeSpecificData?.response,
    featureFlags: { featureFlags, featureFlagLogo },
    serverDataLayer: pageSpecificDatalayer,
  };
};

const postAEMModelOperations = async (context: any, parallelCallsResponse: any, _: any, model: any) => {
  const { log } = global.loggerInstance('postAEMModelOperations');

  log.debug('[postAEMModelOperations] context -', context);
  const { req } = context;
  const sessionID = req?.cookies?.sessionID ?? '';
  const { sessionData } = parallelCallsResponse;
  let ariesSearch: any = sessionData?.cacheData?.data?.AriesSearch;
  log.debug('[postAEMModelOperations] MBE ariesSearch before updation', ariesSearch);
  const ariesSearchCriteria = ariesSearch?.searchCriteria || {};
  const availabilityRequest = ariesSearchCriteria?.availabilityRequestVO;
  const ariesSearchAddress = ariesSearchCriteria?.address;
  const ariesDestination = ariesSearchAddress?.destination;
  const hasDestination = isNonEmptyString(ariesDestination);
  const hasClusterCode = isNonEmptyString(availabilityRequest?.clusterCode, 'CORP');
  const hasCorpCode = isNonEmptyString(availabilityRequest?.corporateCode, 'QRI');
  const hasRateCode = isNonEmptyString(availabilityRequest?.rateCollectionsClusterCode, 'QRI');
  const isOnCycle = context?.mbeOffersCycleStatus === 'On';
  const mbeData = model?.mbeData || {};
  const isMbeDynamicWorkflow = context?.mbeDynamicWorkflow === true;
  const mbeClusterCode = !(hasDestination && hasClusterCode)
    ? isMbeDynamicWorkflow
      ? isOnCycle
        ? 'CORP'
        : 'NONE'
      : mbeData?.clusterCode
    : availabilityRequest?.clusterCode;
  log.debug('[MBE] clusterCode ', mbeClusterCode);

  const mbeCorporateCode = !(hasDestination && hasCorpCode)
    ? isMbeDynamicWorkflow
      ? isOnCycle
        ? context.mbeOffersClusterCode
        : null
      : mbeData?.corporateCode
    : availabilityRequest?.corporateCode;
  const mbeRateCollectionsClusterCode = !(hasDestination && hasRateCode)
    ? isMbeDynamicWorkflow
      ? isOnCycle
        ? context.mbeOffersClusterCode
        : null
      : mbeData?.corporateCode
    : availabilityRequest?.rateCollectionsClusterCode;

  // update sessionData utility server side
  const updateSessionObjfunc = async (ariesSearch: any = {}) => {
    try {
      const url = `${SESSION_APP_DOMAIN_URL}/phoenix-gateway/session`;
      const headers = {
        Cookie: `sessionID=${sessionID}`,
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      };
      const payload = {
        createOrUpdate: {
          AriesSearch: ariesSearch,
        },
        remove: [],
        sessionToken: sessionID,
      };
      await fetch(url, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(payload),
      })
        .then(res => res.json())
        .then(data => {
          log.debug(`[MBE] update session done:, ${data}`);
          return data?.status;
        });
    } catch (error) {
      log.debug(`[MBE] update session API Failed:, ${error}`);
    }
  };
  const updateSessionData = async () => {
    ariesSearch = {
      ...ariesSearch,
      searchCriteria: {
        ...ariesSearchCriteria,
        searchType: 'InCity',
        address: {
          googlePlaceAddress: ariesSearchAddress?.googlePlaceAddress ?? mbeData?.defaultCountryName ?? 'United States',
          stateProvinceDisplayName: ariesSearchAddress?.stateProvinceDisplayName ?? '',
          country: ariesSearchAddress?.country ?? mbeData?.defaultCountryCode ?? 'US',
          destination: ariesSearchAddress?.destination ?? mbeData?.defaultCountryName ?? 'United States',
          destinationType: ariesSearchAddress?.destinationType ?? 'Country',
        },
        distanceInMiles: ariesSearchCriteria?.distanceInMiles ?? true,
        searchRadius: ariesSearchCriteria?.searchRadius ?? 50,
        availabilityRequestVO: {
          ...availabilityRequest,
          checkInDate:
            availabilityRequest?.checkInDate ??
            (() => {
              const now = new Date();
              now.setUTCHours(0, 0, 0, 0);
              const yyyy = now.getUTCFullYear();
              const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
              const dd = String(now.getUTCDate()).padStart(2, '0');
              return `${yyyy}-${mm}-${dd}T00:00:00.000+0000`;
            })(),
          checkOutDate:
            availabilityRequest?.checkOutDate ??
            (() => {
              const now = new Date();
              now.setUTCHours(0, 0, 0, 0);
              const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
              const yyyy = tomorrow.getUTCFullYear();
              const mm = String(tomorrow.getUTCMonth() + 1).padStart(2, '0');
              const dd = String(tomorrow.getUTCDate()).padStart(2, '0');
              return `${yyyy}-${mm}-${dd}T00:00:00.000+0000`;
            })(),
          numRooms: availabilityRequest?.numRooms ?? 1,
          numGuestsPerRoom: availabilityRequest?.numGuestsPerRoom ?? 1,
          numAdultsPerRoom: availabilityRequest?.numAdultsPerRoom ?? 1,
          maxRate: availabilityRequest?.maxRate ?? 0,
          minRate: availabilityRequest?.minRate ?? 0,
          clusterCode: mbeClusterCode,
          groupCode: availabilityRequest?.groupCode ?? '',
          corporateCode: mbeCorporateCode,
          defaultDateSearch: availabilityRequest?.defaultDateSearch ?? false,
          lengthOfStay: availabilityRequest?.lengthOfStay ?? 1,
          defaultTabTotalCost: availabilityRequest?.defaultTabTotalCost ?? false,
          numChildrenPerRoom: availabilityRequest?.numChildrenPerRoom ?? 0,
          rateCollectionsClusterCode: mbeRateCollectionsClusterCode,
          flexibleDate: availabilityRequest?.flexibleDate ?? true,
        },
        countryName: ariesSearchCriteria?.countryName ?? mbeData?.defaultCountryCode ?? 'US',
        destinationAddressPlaceId:
          ariesSearchCriteria?.destinationAddressPlaceId ?? mbeData?.defaultPlaceId ?? 'ChIJCzYy5IS16lQRQrfeQ5K5Oxw',
        destinationAddressSecondaryText:
          ariesSearchCriteria?.destinationAddressSecondaryText ?? `null, ${mbeData?.defaultCountryCode ?? 'US'}`,
        paginationAndSortingRequestVO: {
          sortType: ariesSearchCriteria?.paginationAndSortingRequestVO?.sortType ?? 'city',
          pageNumber: ariesSearchCriteria?.paginationAndSortingRequestVO?.pageNumber ?? 1,
        },
        keywordSearchRequestVO: {
          keywords: ariesSearchCriteria?.keywordSearchRequestVO?.keywords ?? '',
        },
      },
      search_google_places_destination:
        ariesSearch?.search_google_places_destination ??
        `"Name:${mbeData?.defaultCountryName ?? 'United States'}|Locality:|State:|Postal Code:|Country:${
          mbeData?.defaultCountryCode ?? 'US'
        }|Latitude:|Longitude:|Types:|Address:${mbeData?.defaultCountryName ?? 'United States'}"`,
      search_keyword: ariesSearch?.search_keyword ?? mbeData?.defaultCountryName ?? 'United States',
      search_criteria_changed: ariesSearch?.search_criteria_changed ?? true,
      search_dates_flexible: ariesSearch?.search_dates_flexible ?? true,
      search_date_type: ariesSearch?.search_date_type ?? 'Flexible Dates',
    };
    log.debug('[postAEMModelOperations] MBE constructed ariesSearch object', ariesSearch);
    await updateSessionObjfunc(ariesSearch);
  };

  if (!(hasCorpCode && hasClusterCode)) {
    await updateSessionData();
  }

  const items = model.cqItems?.root?.[':items']?.responsivegrid?.[':items'];
  Object.keys(items).forEach(key => {
    const component = items[key];
    if (component[':type'] === 'mi-aem-offers-spa/components/onoffcontainer') {
      const items2 = component?.[':items'];
      Object.keys(items2).forEach(key2 => {
        const component2 = items2[key2];
        if (component2[':type'] === 'mi-aem-shop-spa/components/content/baselinesearchform/v1/baselinesearchform') {
          component2.numberOfSearchForms = 1;
        }
      });
    }
  });
  return model;
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  mbeSpecificData,
  {},
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);

export default pageRenderer;
